/**
 * Tire Search — full search results page controller.
 * Handles vehicle selection, accordion filters, range sliders, and mobile drawer.
 * Fetches from /api/v2/tires with faceted filtering, sorting, and pagination.
 */

import { getVehicle, saveVehicle, clearVehicle } from './vehicle-store.js';

const API_BASE_FALLBACK = 'https://static.218.250.90.157.clients.your-server.de';

const SEASON_LABELS = {
  summer: 'Sommerdekk',
  winter: 'Vinterdekk',
  'winter-friction': 'Vinterdekk (friksjon)',
  studded: 'Piggdekk',
  allseason: 'Helårsdekk',
};

class TireSearchElement extends HTMLElement {
  connectedCallback() {
    this._api = this.dataset.apiBase || API_BASE_FALLBACK;
    this._pageSize = parseInt(this.dataset.pageSize, 10) || 24;
    this._stateKey = `fh_ts_state:${window.location.pathname}`;

    // Default filters from Shopify theme settings
    this._defaultCategory = this.dataset.defaultCategory || '';
    this._defaultBrand = this.dataset.defaultBrand || '';

    // DOM refs
    this._header = this.querySelector('[data-role="header"]');
    this._headerSelector = this.querySelector('[data-role="header-selector"]');
    this._headerVehicle = this.querySelector('[data-role="header-vehicle"]');
    this._vehicleLabel = this.querySelector('[data-role="vehicle-label"]');
    this._grid = this.querySelector('[data-role="grid"]');
    this._loading = this.querySelector('[data-role="loading"]');
    this._empty = this.querySelector('[data-role="empty"]');
    this._countEl = this.querySelector('[data-role="count"]');
    this._pagination = this.querySelector('[data-role="pagination"]');
    this._sortSelect = this.querySelector('[data-role="sort"]');
    this._searchInput = this.querySelector('[data-role="search-input"]');
    this._searchBar = this.querySelector('[data-role="search-bar"]');

    // Inline vehicle selector refs
    this._hMake = this.querySelector('[data-role="h-make"]');
    this._hModel = this.querySelector('[data-role="h-model"]');
    this._hYear = this.querySelector('[data-role="h-year"]');
    this._hTrim = this.querySelector('[data-role="h-trim"]');
    this._hSearch = this.querySelector('[data-role="h-search"]');

    // Regnr refs (header)
    this._hRegnr = this.querySelector('[data-role="h-regnr"]');
    this._hRegnrSearch = this.querySelector('[data-role="h-regnr-search"]');

    // Filter selects (dropdowns)
    this._filters = {
      width: this.querySelector('[data-filter="width"]'),
      profile: this.querySelector('[data-filter="profile"]'),
      diameter: this.querySelector('[data-filter="diameter"]'),
    };

    // Accordion checkbox filters
    this._accordions = {
      category: this.querySelector('[data-accordion="category"]'),
      brand: this.querySelector('[data-accordion="brand"]'),
      tire_model: this.querySelector('[data-accordion="tire_model"]'),
      load_index: this.querySelector('[data-accordion="load_index"]'),
      speed_index: this.querySelector('[data-accordion="speed_index"]'),
      rolling_resistance: this.querySelector('[data-accordion="rolling_resistance"]'),
      wet_grip: this.querySelector('[data-accordion="wet_grip"]'),
      noise: this.querySelector('[data-accordion="noise"]'),
    };

    // Mobile tyre brand selector — same compact flow as the wheel page.
    this._ensureProfessionalTireStyles();
    this._initProfessionalTireUi();

    // Range filter inputs
    this._rangeInputs = {
      min_price: this.querySelector('[data-filter="min_price"]'),
      max_price: this.querySelector('[data-filter="max_price"]'),
    };

    // Range sliders
    this._sliders = {};
    for (const el of this.querySelectorAll('[data-slider]')) {
      const key = el.dataset.slider;
      this._sliders[key] = {
        container: el,
        thumbMin: el.querySelector('[data-role="thumb-min"]'),
        thumbMax: el.querySelector('[data-role="thumb-max"]'),
        fill: el.querySelector('[data-role="slider-fill"]'),
        valMin: el.querySelector('[data-role="slider-min"]'),
        valMax: el.querySelector('[data-role="slider-max"]'),
      };
    }

    // Active filter chips
    this._activeFiltersEl = this.querySelector('[data-role="active-filters"]');

    // Tyre size buttons
    this._diameterBar = this.querySelector('[data-role="diameter-bar"]');
    this._selectedTyreSize = null;
    this._selectedStaggeredPair = null; // { front: {width,profile,diameter}, rear: {width,profile,diameter} }

    // Specs panel
    this._specsPanel = this.querySelector('[data-role="specs-panel"]');
    this._specsTable = this.querySelector('[data-role="specs-table"]');
    this._specsToggle = this.querySelector('[data-role="toggle-specs"]');

    // Mobile filter drawer
    const filterToggle = this.querySelector('[data-role="filter-toggle"]');
    if (filterToggle) {
      const label =
        filterToggle.querySelector('[data-role="filter-label"]') ||
        filterToggle.querySelector('span:last-child');

      if (label) label.textContent = 'Filter';
      else filterToggle.textContent = 'Filter';

      filterToggle.setAttribute('aria-label', 'Åpne dekkfilter');
    }

    const filterBackdrop = this.querySelector('[data-role="filter-backdrop"]');
    const filterClose = this.querySelector('[data-role="filter-close"]');
    this._sidebarCount = this.querySelector('[data-role="sidebar-count"]');
    const sidebar = this.querySelector('[data-role="sidebar"]');
    if (filterToggle && sidebar) {
      const open = () => { sidebar.classList.add('ts__sidebar--open'); if (filterBackdrop) filterBackdrop.hidden = false; };
      const close = () => { sidebar.classList.remove('ts__sidebar--open'); if (filterBackdrop) filterBackdrop.hidden = true; };
      filterToggle.addEventListener('click', open);
      filterClose?.addEventListener('click', close);
      filterBackdrop?.addEventListener('click', close);
    }

    // Diameter bar click handler (tyre size buttons — single and staggered pair)
    if (this._diameterBar) {
      this._diameterBar.addEventListener('click', (e) => {
        // Staggered pair button
        const pairBtn = e.target.closest('[data-tyre-pair]');
        if (pairBtn) {
          const pairKey = pairBtn.dataset.tyrePair;
          if (this._selectedStaggeredPair && `${this._selectedStaggeredPair._key}` === pairKey) {
            // Deselect pair
            this._clearTyreSizeSelection(true);
          } else {
            this._selectedTyreSize = null;
            this._selectedStaggeredPair = {
              _key: pairKey,
              front: { width: +pairBtn.dataset.frontW, profile: +pairBtn.dataset.frontP, diameter: +pairBtn.dataset.frontD },
              rear: { width: +pairBtn.dataset.rearW, profile: +pairBtn.dataset.rearP, diameter: +pairBtn.dataset.rearD },
            };
            this._diameterBar.querySelectorAll('.ts__diam-btn').forEach((b) =>
              b.classList.toggle('ts__diam-btn--active', b.dataset.tyrePair === pairKey)
            );
            // Clear dropdown values so _search() uses tyre_sizes path
            if (this._filters.width) this._filters.width.value = '';
            if (this._filters.profile) this._filters.profile.value = '';
            if (this._filters.diameter) this._filters.diameter.value = '';
          }
          this._page = 1;
          this._search();
          return;
        }

        // Single size button
        const btn = e.target.closest('[data-tyre-key]');
        if (!btn) return;
        const key = btn.dataset.tyreKey;
        if (this._selectedTyreSize === key) {
          // Deselect — clear dropdowns back to default
          this._clearTyreSizeSelection(true);
        } else {
          // Select this size — set all 3 filters
          this._selectedTyreSize = key;
          this._selectedStaggeredPair = null;
          this._diameterBar.querySelectorAll('.ts__diam-btn').forEach((b) =>
            b.classList.toggle('ts__diam-btn--active', b.dataset.tyreKey === key)
          );
          if (this._filters.width) this._filters.width.value = btn.dataset.tyreWidth || '';
          if (this._filters.profile) this._filters.profile.value = btn.dataset.tyreProfile || '';
          if (this._filters.diameter) this._filters.diameter.value = btn.dataset.tyreDiam || '';
        }
        this._page = 1;
        this._search();
      });
    }

    // Reset button(s)
    const resetHandler = () => {
      Object.values(this._filters).forEach((sel) => { if (sel) sel.value = ''; });
      this._clearTyreSizeSelection();
      this._resetAccordions();
      this._updateMobileTireBrandTrigger();
      Object.values(this._rangeInputs).forEach((input) => { if (input) input.value = ''; });
      this._resetSlider('price');
      this._page = 1;
      this._search();
    };
    this.querySelectorAll('[data-role="filter-reset"]').forEach(btn => btn.addEventListener('click', resetHandler));

    // Dropdown filter change events — clear tyre size button selection on manual change
    Object.values(this._filters).forEach((sel) => {
  if (!sel) return;

  sel.addEventListener('change', () => {
    this._clearTyreSizeSelection();
    this._page = 1;
    this._search();
  });
});
const sizeAccordion = this.querySelector('[data-accordion="size"] .ts__accordion-toggle');

if (sizeAccordion) {
  sizeAccordion.addEventListener('click', () => {
    Object.values(this._filters).forEach((sel) => {
      if (sel) sel.value = '';
    });

    this._clearTyreSizeSelection();
    this._page = 1;
    this._search();
  });
}

    // Accordion checkbox change events
    Object.entries(this._accordions).forEach(([key, acc]) => {
      if (!acc) return;
      acc.addEventListener('change', (e) => {
        if (e.target.type !== 'checkbox') return;

        if (key === 'brand') {
          // Mobile selector is single-choice. Keep desktop checkbox state in sync.
          acc.querySelectorAll('input[type="checkbox"]').forEach((checkbox) => {
            if (checkbox !== e.target) checkbox.checked = false;
          });
          this._updateMobileTireBrandTrigger();
        }

        this._page = 1;
        this._search();
      });
    });

    // Sort select
    if (this._sortSelect) {
  this._sortSelect.value = 'price_asc';
this._sort = 'price_asc';

  this._sortSelect.addEventListener('change', () => {
    this._sort = this._sortSelect.value;
    this._page = 1;
    this._search();
  });
}

    // Shared vehicle data arrays
    this._makes = [];
    this._models = [];
    this._trims = [];

    // State
    this._vehicle = null;
    this._vehicleSpecs = null;
    this._vehicleTyre = null;
    this._queryText = '';
    this._rangeDebounce = null;
    this._page = 1;
    this._sort = 'price_asc';
    this._defaultsApplied = false;

    // Accordion toggle (expand/collapse)
    this.querySelectorAll('.ts__accordion-toggle').forEach(btn => {
      btn.addEventListener('click', () => {
        const body = btn.nextElementSibling;
        body.classList.toggle('is-collapsed');
        btn.querySelector('.ts__accordion-chevron').classList.toggle('is-rotated');
      });
    });

    // Accordion search input filtering
    this.querySelectorAll('[data-role="accordion-search"]').forEach(searchInput => {
      searchInput.addEventListener('input', () => {
        const accKey = searchInput.closest('[data-accordion]')?.dataset.accordion;
        if (accKey) this._applyCheckboxFilter(accKey);
      });
    });

    // Range slider events
    this._initSlider('price', 'min_price', 'max_price');

    // Vehicle header buttons
    this.querySelector('[data-role="change-vehicle"]')?.addEventListener(
      'click', () => this._changeVehicle()
    );
    this.querySelector('[data-role="clear-vehicle"]')?.addEventListener(
      'click', () => this._clearVehicle()
    );
    this._specsToggle?.addEventListener('click', () => {
      if (!this._specsPanel) return;
      const open = this._specsPanel.hidden;
      this._specsPanel.hidden = !open;
      this._specsToggle.classList.toggle('ts__header-btn--active', open);
      const specsLabel = open ? 'Spesifikasjoner \u25B2' : 'Spesifikasjoner \u25BC';
      const specsInner = this._specsToggle.querySelector('.ts__header-btn-top');
      if (specsInner) specsInner.textContent = specsLabel;
      else this._specsToggle.textContent = specsLabel;
    });

    // Search input
    if (this._searchInput) {
      this._searchInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          this._onTextSearch();
        }
      });
    }
    this.querySelector('[data-role="search-btn"]')?.addEventListener(
      'click', () => this._onTextSearch()
    );

    // Regnr event listeners
    if (this._hRegnr) {
      this._hRegnr.addEventListener('input', () => {
        const val = this._hRegnr.value.replace(/[\s-]/g, '');
        if (this._hRegnrSearch) {
          this._hRegnrSearch.disabled = val.length < 4;
          if (val.length >= 4) this._hRegnrSearch.dataset.revealed = '';
          else delete this._hRegnrSearch.dataset.revealed;
        }
      });
      this._hRegnr.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' && !this._hRegnrSearch?.disabled) this._onRegnrSearch();
      });
    }
    if (this._hRegnrSearch) this._hRegnrSearch.addEventListener('click', () => this._onRegnrSearch());

    // Inline vehicle selector events
    this._initInlineSelector();
    this._updateMobileSteps();
    this._init();
  }


  _ensureProfessionalTireStyles() {
    const styleId = 'ts-professional-mobile-brand-v1';
    if (document.getElementById(styleId)) return;

    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      tire-search .ts-pro-mobile-controls {
        display: none;
      }

      .ts-pro-brand-backdrop {
        position: fixed !important;
        inset: 0 !important;
        z-index: 2147483000 !important;
        background: rgba(8, 10, 14, .48) !important;
        -webkit-backdrop-filter: blur(2px) !important;
        backdrop-filter: blur(2px) !important;
        opacity: 0 !important;
        pointer-events: none !important;
        transition: opacity .18s ease !important;
      }

      .ts-pro-brand-backdrop.is-open {
        opacity: 1 !important;
        pointer-events: auto !important;
      }

      .ts-pro-brand-sheet {
        position: fixed !important;
        left: 50% !important;
        top: 12vh !important;
        z-index: 2147483001 !important;
        width: min(520px, calc(100vw - 64px)) !important;
        max-height: 72vh !important;
        display: flex !important;
        flex-direction: column !important;
        overflow: hidden !important;
        border: 1px solid rgba(206, 161, 69, .35) !important;
        border-radius: 28px !important;
        background: rgba(25, 22, 20, .84) !important;
        -webkit-backdrop-filter: blur(24px) saturate(120%) !important;
        backdrop-filter: blur(24px) saturate(120%) !important;
        box-shadow: 0 24px 70px rgba(0, 0, 0, .34) !important;
        transform: translate(-50%, -18px) scale(.98) !important;
        opacity: 0 !important;
        pointer-events: none !important;
        transition: transform .2s ease, opacity .18s ease !important;
      }

      .ts-pro-brand-sheet.is-open {
        transform: translate(-50%, 0) scale(1) !important;
        opacity: 1 !important;
        pointer-events: auto !important;
      }

      .ts-pro-brand-sheet__scroll {
        max-height: 72vh !important;
        overflow-y: auto !important;
        overflow-x: hidden !important;
        -webkit-overflow-scrolling: touch !important;
        padding: 10px !important;
      }

      .ts-pro-brand-sheet__title {
        padding: 8px 18px 5px !important;
        color: rgba(255, 255, 255, .58) !important;
        font-size: 11px !important;
        font-weight: 800 !important;
        letter-spacing: .06em !important;
        text-transform: uppercase !important;
      }

      .ts-pro-brand-option {
        width: 100% !important;
        min-height: 58px !important;
        display: flex !important;
        align-items: center !important;
        justify-content: space-between !important;
        gap: 12px !important;
        padding: 12px 20px !important;
        border: 0 !important;
        border-bottom: 1px solid rgba(255, 255, 255, .08) !important;
        background: transparent !important;
        color: #fff !important;
        text-align: left !important;
        font-size: 18px !important;
        font-weight: 500 !important;
        cursor: pointer !important;
      }

      .ts-pro-brand-option:last-child {
        border-bottom: 0 !important;
      }

      .ts-pro-brand-option.is-selected {
        background: rgba(202, 155, 59, .14) !important;
      }

      .ts-pro-brand-option__mark {
        color: #fff !important;
        font-size: 20px !important;
        font-weight: 800 !important;
      }

      body.ts-pro-sheet-open {
        overflow: hidden !important;
      }

      @media (max-width: 749px) {
        tire-search [data-role="header"] {
          padding: 10px 12px 9px !important;
          border-radius: 12px !important;
        }

        tire-search [data-role="vehicle-label"] {
          font-size: 12px !important;
          font-weight: 800 !important;
          letter-spacing: .02em !important;
          line-height: 1.2 !important;
        }

        tire-search [data-role="header"] [data-role="toggle-specs"],
        tire-search [data-role="header"] [data-role="change-vehicle"],
        tire-search [data-role="header"] [data-role="clear-vehicle"],
        tire-search [data-role="header"] [data-role="filter-reset"] {
          display: none !important;
        }

        tire-search .ts-pro-mobile-controls {
          width: 100% !important;
          display: block !important;
          margin-top: 7px !important;
        }

        tire-search .ts-pro-mobile-toolbar {
          width: 100% !important;
          display: grid !important;
          grid-template-columns: 1fr 1fr !important;
          gap: 6px !important;
          align-items: stretch !important;
        }

        tire-search .ts-pro-mobile-brand-trigger {
          width: 100% !important;
          min-width: 0 !important;
          min-height: 40px !important;
          display: flex !important;
          align-items: center !important;
          justify-content: space-between !important;
          gap: 6px !important;
          padding: 8px 9px !important;
          border: 1px solid rgba(255, 255, 255, .14) !important;
          border-radius: 7px !important;
          background: rgba(255, 255, 255, .04) !important;
          color: #fff !important;
          box-sizing: border-box !important;
          font-size: 12px !important;
          font-weight: 700 !important;
          cursor: pointer !important;
        }

        tire-search .ts-pro-mobile-brand-trigger__left {
          min-width: 0 !important;
          display: inline-flex !important;
          align-items: center !important;
          gap: 9px !important;
        }

        tire-search .ts-pro-mobile-brand-trigger__label {
          overflow: hidden !important;
          text-overflow: ellipsis !important;
          white-space: nowrap !important;
        }

        tire-search .ts-pro-mobile-action {
          min-width: 0 !important;
          min-height: 38px !important;
          white-space: nowrap !important;
          padding: 7px 6px !important;
          border: 1px solid rgba(255, 255, 255, .14) !important;
          border-radius: 7px !important;
          background: rgba(255, 255, 255, .04) !important;
          color: #fff !important;
          font-size: 10px !important;
          font-weight: 700 !important;
          line-height: 1.1 !important;
          cursor: pointer !important;
        }

        tire-search .ts-pro-mobile-action--specs,
        tire-search .ts-pro-mobile-action--filter {
          border-color: rgba(202, 155, 59, .88) !important;
          background: #050505 !important;
          color: #fff !important;
        }

        tire-search .ts-pro-mobile-action--filter {
          display: inline-flex !important;
          align-items: center !important;
          justify-content: center !important;
          gap: 7px !important;
        }

        tire-search .ts-pro-mobile-action--filter svg {
          width: 15px !important;
          height: 15px !important;
          stroke: #fff !important;
        }

        /* The original filter button remains as the drawer trigger,
           but is hidden visually because the new top Filter button controls it. */
        tire-search .ts__diameter-row > [data-role="filter-toggle"] {
          display: none !important;
        }

        tire-search [data-role="filter-toggle"] {
          display: inline-flex !important;
          align-items: center !important;
          justify-content: center !important;
          gap: 7px !important;
          min-height: 40px !important;
          padding: 8px 14px !important;
          border: 1px solid rgba(202, 155, 59, .85) !important;
          border-radius: 8px !important;
          background: #fff !important;
          color: #171717 !important;
          font-size: 13px !important;
          font-weight: 800 !important;
          line-height: 1 !important;
          cursor: pointer !important;
        }

        tire-search [data-role="filter-toggle"] svg {
          width: 17px !important;
          height: 17px !important;
        }

        tire-search .ts-pro-mobile-toolbar {
          display: grid !important;
          grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) !important;
          gap: 7px !important;
          width: 100% !important;
        }

        tire-search .ts-pro-mobile-action--specs,
        tire-search .ts-pro-mobile-action--filter {
          display: inline-flex !important;
          align-items: center !important;
          justify-content: center !important;
          gap: 7px !important;
          width: 100% !important;
          min-width: 0 !important;
          min-height: 40px !important;
          padding: 8px 10px !important;
          border: 1px solid rgba(200,160,81,.88) !important;
          border-radius: 8px !important;
          background: #050505 !important;
          color: #fff !important;
          box-sizing: border-box !important;
          font-size: 11.5px !important;
          font-weight: 800 !important;
          line-height: 1 !important;
          white-space: nowrap !important;
          opacity: 1 !important;
          visibility: visible !important;
        }

        tire-search .ts-pro-mobile-action--filter svg {
          display: block !important;
          width: 15px !important;
          height: 15px !important;
          flex: 0 0 15px !important;
          stroke: currentColor !important;
        }

      }
    `;
    document.head.appendChild(style);
  }

  _initProfessionalTireUi() {
    this._tireBrandFacetItems = [];

    this._mobileTireControls = document.createElement('div');
    this._mobileTireControls.className = 'ts-pro-mobile-controls';
    this._mobileTireControls.innerHTML = `
      <div class="ts-pro-mobile-toolbar">
        <button type="button"
          class="ts-pro-mobile-action ts-pro-mobile-action--specs"
          data-ts-pro-action="specs">
          Spesifikasjoner
        </button>

        <button type="button"
          class="ts-pro-mobile-action ts-pro-mobile-action--filter"
          data-ts-pro-action="filter"
          aria-label="Åpne dekkfilter">
          <svg aria-hidden="true" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" stroke-width="2" stroke-linecap="round">
            <line x1="4" y1="6" x2="20" y2="6"></line>
            <line x1="7" y1="12" x2="17" y2="12"></line>
            <line x1="10" y1="18" x2="14" y2="18"></line>
          </svg>
          <span>Filter</span>
        </button>
      </div>
    `;

    this._header?.appendChild(this._mobileTireControls);

    this._mobileTireBrandLabel =
      this._mobileTireControls.querySelector('[data-ts-pro-brand-label]');

    this._mobileTireControls
      .querySelector('[data-ts-pro-action="specs"]')
      ?.addEventListener('click', () => this._specsToggle?.click());

    this._mobileTireControls
      .querySelector('[data-ts-pro-action="filter"]')
      ?.addEventListener('click', () => {
        const sidebar = this.querySelector('[data-role="sidebar"]');
        const backdrop = this.querySelector('[data-role="filter-backdrop"]');

        if (!sidebar) return;

        sidebar.classList.add('ts__sidebar--open');
        if (backdrop) backdrop.hidden = false;
      });

    this._createMobileTireBrandSheet();
    this._updateMobileTireBrandTrigger();
  }

  _createMobileTireBrandSheet() {
    this._tireBrandBackdrop = document.createElement('div');
    this._tireBrandBackdrop.className = 'ts-pro-brand-backdrop';

    this._tireBrandSheet = document.createElement('div');
    this._tireBrandSheet.className = 'ts-pro-brand-sheet';
    this._tireBrandSheet.setAttribute('role', 'dialog');
    this._tireBrandSheet.setAttribute('aria-modal', 'true');
    this._tireBrandSheet.setAttribute('aria-label', 'Velg dekkmerke');
    this._tireBrandSheet.innerHTML = `
      <div class="ts-pro-brand-sheet__scroll">
        <div class="ts-pro-brand-sheet__title">Dekkmerker</div>
        <div data-ts-pro-brand-list></div>
      </div>
    `;

    document.body.appendChild(this._tireBrandBackdrop);
    document.body.appendChild(this._tireBrandSheet);

    this._tireBrandList =
      this._tireBrandSheet.querySelector('[data-ts-pro-brand-list]');

    this._tireBrandBackdrop.addEventListener(
      'click',
      () => this._closeMobileTireBrandSheet()
    );

    document.addEventListener('keydown', (event) => {
      if (
        event.key === 'Escape' &&
        this._tireBrandSheet?.classList.contains('is-open')
      ) {
        this._closeMobileTireBrandSheet();
      }
    });
  }

  _getSelectedTireBrand() {
    const checked = [
      ...(this._accordions?.brand?.querySelectorAll(
        'input[type="checkbox"]:checked'
      ) || [])
    ];

    if (checked.length === 1) return checked[0].value;
    return '';
  }

  _updateMobileTireBrandTrigger() {
    if (!this._mobileTireBrandLabel) return;
    this._mobileTireBrandLabel.textContent =
      this._getSelectedTireBrand() || 'Velg merke';
  }

  _openMobileTireBrandSheet() {
    this._renderMobileTireBrandSheet();
    this._tireBrandBackdrop?.classList.add('is-open');
    this._tireBrandSheet?.classList.add('is-open');
    document.body.classList.add('ts-pro-sheet-open');
  }

  _closeMobileTireBrandSheet() {
    this._tireBrandBackdrop?.classList.remove('is-open');
    this._tireBrandSheet?.classList.remove('is-open');
    document.body.classList.remove('ts-pro-sheet-open');
  }

  _renderMobileTireBrandSheet() {
    if (!this._tireBrandList) return;

    const selectedBrand = this._getSelectedTireBrand();
    const uniqueBrands = [];
    const seen = new Set();

    for (const item of this._tireBrandFacetItems || []) {
      const name = String(item?.value || '').trim();
      const normalized = name.toLowerCase();
      if (!name || seen.has(normalized)) continue;
      seen.add(normalized);
      uniqueBrands.push(name);
    }

    uniqueBrands.sort((a, b) => a.localeCompare(b, 'nb'));

    const escape = (value) => String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');

    this._tireBrandList.innerHTML = [
      `<button type="button" class="ts-pro-brand-option${!selectedBrand ? ' is-selected' : ''}" data-ts-pro-brand="">
        <span>Alle merker</span>
        <span class="ts-pro-brand-option__mark">${!selectedBrand ? '✓' : '›'}</span>
      </button>`,
      ...uniqueBrands.map((name) => `
        <button type="button" class="ts-pro-brand-option${selectedBrand === name ? ' is-selected' : ''}" data-ts-pro-brand="${escape(name)}">
          <span>${escape(name)}</span>
          <span class="ts-pro-brand-option__mark">${selectedBrand === name ? '✓' : '›'}</span>
        </button>
      `)
    ].join('');

    this._tireBrandList
      .querySelectorAll('[data-ts-pro-brand]')
      .forEach((button) => {
        button.addEventListener('click', () => {
          this._selectTireBrandFromMobile(
            button.dataset.tsProBrand || ''
          );
        });
      });
  }

  _selectTireBrandFromMobile(brandName) {
    const brandAccordion = this._accordions?.brand;

    brandAccordion
      ?.querySelectorAll('input[type="checkbox"]')
      .forEach((checkbox) => {
        checkbox.checked =
          !!brandName && checkbox.value === brandName;
      });

    this._updateMobileTireBrandTrigger();
    this._closeMobileTireBrandSheet();
    this._page = 1;
    this._search();
  }

  // ── Initialization ───────────────────────────────────────────────────

  _init() {
    // If the customer came from a wheel selection, keep only tyre options
    // with the same rim diameter (for example 20" wheels => only 20" tyres).
    const storedWheelDiam = localStorage.getItem('sbfWheelSize');
    const parsedWheelDiam = Number.parseInt(storedWheelDiam || '', 10);
    this._pendingWheelDiam = Number.isFinite(parsedWheelDiam) ? parsedWheelDiam : null;
    localStorage.removeItem('sbfWheelSize');

    const params = new URLSearchParams(window.location.search);
    const make = params.get('make');
    const model = params.get('model');
    const trim = params.get('trim');
    const regnr = params.get('regnr');
    const q = params.get('q');

    // Restore session state only when landing context does not provide explicit defaults/filters
    this._restoredAccordions = {};
    const stateBlockingParams = [
      'q', 'category', 'brand', 'tire_model', 'load_index', 'speed_index', 'rolling_resistance', 'wet_grip', 'noise',
      'width', 'profile', 'diameter', 'min_price', 'max_price',
      'make', 'model', 'trim', 'regnr'
    ];
    const hasBlockingUrlParams = stateBlockingParams.some((key) => params.has(key));
    const canRestoreSessionState = !hasBlockingUrlParams && !this._defaultCategory && !this._defaultBrand;

    if (canRestoreSessionState) {
      try {
        const stateStr = sessionStorage.getItem(this._stateKey);
        if (stateStr) {
          const state = JSON.parse(stateStr);
          if (state.sort && this._sortSelect) {
            this._sort = state.sort;
            this._sortSelect.value = state.sort;
          }
          if (state.selectedTyreSize) this._selectedTyreSize = state.selectedTyreSize;
          if (state.selectedStaggeredPair) this._selectedStaggeredPair = state.selectedStaggeredPair;

          if (state.width && this._filters.width) this._filters.width.value = state.width;
          if (state.profile && this._filters.profile) this._filters.profile.value = state.profile;
          if (state.diameter && this._filters.diameter) this._filters.diameter.value = state.diameter;

          this._restoredAccordions = {
            category: state.category || [],
            brand: state.brand || [],
            tire_model: state.tire_model || [],
            load_index: state.load_index || [],
            speed_index: state.speed_index || [],
            rolling_resistance: state.rolling_resistance || [],
            wet_grip: state.wet_grip || [],
            noise: state.noise || []
          };

          if (state.ranges) {
            for (const [key, val] of Object.entries(state.ranges)) {
              if (this._rangeInputs[key]) this._rangeInputs[key].value = val;
            }
          }
        }
      } catch (e) { console.warn('Failed to restore filter state', e); }
    }

    // Direct tyre size search (from homepage "Søk etter dekk" tab)
    const urlWidth = params.get('width');
    const urlProfile = params.get('profile');
    const urlDiameter = params.get('diameter');
    if (urlWidth || urlProfile || urlDiameter) {
      this._pendingFilters = { width: urlWidth, profile: urlProfile, diameter: urlDiameter };
      this._showVehicleSelector();
      this._search();
      return;
    }

    // Regnr mode — look up plate, set vehicle, search
    if (regnr) {
      this._initFromRegnr(regnr);
      return;
    }

    // Text search mode
    if (q) {
      this._queryText = q;
      if (this._searchInput) this._searchInput.value = q;
      if (this._searchBar) this._searchBar.hidden = false;
    }

    // Vehicle mode
    if (make && model && trim) {
      const saved = getVehicle();
      if (saved && saved.makeSlug === make && saved.modelSlug === model && saved.trimId === trim) {
        this._vehicle = saved;
      } else {
        this._vehicle = { makeSlug: make, modelSlug: model, trimId: trim };
      }
      this._showVehicleHeader();
      this._fetchVehicleSpecsThenSearch();
    } else if (!q) {
      const saved = getVehicle();
      if (saved?.makeSlug) {
        this._vehicle = saved;
        this._showVehicleHeader();
        this._fetchVehicleSpecsThenSearch();
      } else {
        this._showVehicleSelector();
        this._search();
      }
    } else {
      this._showVehicleSelector();
      this._search();
    }
  }

  async _initFromRegnr(plate) {
    // Check if saved vehicle matches this plate
    const saved = getVehicle();
    if (saved?.regnr === plate && saved.specs) {
      this._vehicle = saved;
      this._showVehicleHeader();
      if (saved.specsRaw) {
        this._vehicleSpecs = saved.specsRaw;
        this._buildSpecsTable(saved.specsRaw);
        this._buildTyreSizeButtons();
      }

      // A registration-number result can contain only the dimensions from
      // the vehicle register. When the plate has been matched to a vehicle
      // trim, enrich it with the complete manual vehicle data as well.
      const hasMatchedTrim =
        saved.makeSlug &&
        saved.modelSlug &&
        saved.trimId &&
        !String(saved.trimId).startsWith('regnr:');

      if (hasMatchedTrim) this._fetchVehicleSpecsThenSearch();
      else this._search();
      return;
    }

    // Fetch from regnr API
    try {
      const res = await fetch(`${this._api}/api/vehicles/regnr/${encodeURIComponent(plate)}`);
      const json = await res.json();
      if (!res.ok || !json.success) {
        this._showVehicleSelector();
        this._search();
        return;
      }

      const meta = json.meta;
      this._vehicle = {
        makeSlug: meta.make_slug,
        makeName: meta.make,
        modelSlug: meta.model_slug,
        modelName: meta.model,
        year: meta.year ? String(meta.year) : '',
        trimId: meta.trim_id ? String(meta.trim_id) : `regnr:${plate}`,
        trimName: meta.model,
        regnr: plate,
        regnrSource: 'vegvesen',
      };

      this._vehicleSpecs = json.data;
      const specs = this._parseSpecs(json.data);
      this._vehicle.specs = specs;
      this._vehicle.specsRaw = json.data;
      saveVehicle(this._vehicle);

      this._showVehicleHeader();
      this._buildSpecsTable(json.data);
      this._buildTyreSizeButtons();
      this._syncFiltersFromVehicle();

      if (meta.csv_match && meta.trim_id) {
        this._fetchVehicleSpecsThenSearch();
      } else {
        this._search();
      }
    } catch (e) {
      console.error('Regnr init failed', e);
      this._showVehicleSelector();
      this._search();
    }
  }

  async _onRegnrSearch() {
    const plate = (this._hRegnr?.value || '').replace(/[\s-]/g, '').toUpperCase();
    if (plate.length < 4) return;

    if (this._hRegnrSearch) this._hRegnrSearch.disabled = true;

    try {
      const res = await fetch(`${this._api}/api/vehicles/regnr/${encodeURIComponent(plate)}`);
      const json = await res.json();

      if (!res.ok || !json.success) {
        if (this._hRegnrSearch) this._hRegnrSearch.disabled = false;
        return;
      }

      const meta = json.meta;
      this._vehicle = {
        makeSlug: meta.make_slug,
        makeName: meta.make,
        modelSlug: meta.model_slug,
        modelName: meta.model,
        year: meta.year ? String(meta.year) : '',
        trimId: meta.trim_id ? String(meta.trim_id) : `regnr:${plate}`,
        trimName: meta.model,
        regnr: plate,
        regnrSource: 'vegvesen',
      };

      this._vehicleSpecs = json.data;
      const specs = this._parseSpecs(json.data);
      this._vehicle.specs = specs;
      this._vehicle.specsRaw = json.data;
      saveVehicle(this._vehicle);

      // Update URL
      const urlParams = new URLSearchParams({ regnr: plate });
      if (this._queryText) urlParams.set('q', this._queryText);
      window.history.replaceState({}, '', `${window.location.pathname}?${urlParams}`);

      this._showVehicleHeader();
      this._clearTyreSizeSelection(true);
      this._page = 1;
      Object.values(this._filters).forEach((sel) => { if (sel) sel.value = ''; });
      this._resetAccordions();
      Object.values(this._rangeInputs).forEach((input) => { if (input) input.value = ''; });
      this._resetSlider('price');

      this._buildSpecsTable(json.data);
      this._buildTyreSizeButtons();
      this._syncFiltersFromVehicle();

      if (meta.csv_match && meta.trim_id) {
        this._fetchVehicleSpecsThenSearch();
      } else {
        this._search();
      }
    } catch (e) {
      console.error('Regnr search failed', e);
      if (this._hRegnrSearch) this._hRegnrSearch.disabled = false;
    }
  }

  _onTextSearch() {
    const val = this._searchInput?.value.trim() || '';
    this._queryText = val;
    this._page = 1;

    // Update URL
    const urlParams = new URLSearchParams(window.location.search);
    if (val) urlParams.set('q', val);
    else urlParams.delete('q');
    const qs = urlParams.toString();
    window.history.replaceState({}, '', qs ? `${window.location.pathname}?${qs}` : window.location.pathname);

    this._search();
  }

  _showEmptyState() {
    if (this._loading) this._loading.hidden = true;
    if (this._empty) this._empty.hidden = false;
    if (this._countEl) this._countEl.textContent = '';
  }

  // ── Vehicle specs ─────────────────────────────────────────────────────

  _mergeRegnrAndTrimSpecs(regnrData, trimData) {
    if (!regnrData || typeof regnrData !== 'object') return trimData;
    if (!trimData || typeof trimData !== 'object') return regnrData;

    // Keep the registration/Vegvesen chassis as the authoritative base because
    // it contains the exact registered setup and axle-load information. Add the
    // matched trim's complete OEM/alternative options used by the manual search.
    const merged = { ...trimData, ...regnrData };

    merged.DRDChassisReturn =
      regnrData.DRDChassisReturn || trimData.DRDChassisReturn;

    merged.DRDModelReturn =
      trimData.DRDModelReturn || regnrData.DRDModelReturn;

    return merged;
  }

  async _fetchVehicleSpecsThenSearch() {
    if (!this._vehicle) return this._search();

    const { makeSlug, modelSlug, trimId } = this._vehicle;
    const hasMatchedTrim =
      makeSlug &&
      modelSlug &&
      trimId &&
      !String(trimId).startsWith('regnr:');

    // A plate without a CSV/trim match can only use the registered raw data.
    if (!hasMatchedTrim) {
      if (this._vehicle.specsRaw) {
        const data = this._vehicle.specsRaw;
        this._vehicleSpecs = data;
        this._vehicle.specs = this._parseSpecs(data);
        this._buildSpecsTable(data);
        this._buildTyreSizeButtons();
        this._syncFiltersFromVehicle();
      }
      return this._search();
    }

    try {
      const res = await fetch(
        `${this._api}/api/vehicles/makes/${makeSlug}/models/${modelSlug}/${trimId}`
      );
      if (res.ok) {
        const json = await res.json();
        const trimData = json.data ?? json;
        const data = this._vehicle.specsRaw
          ? this._mergeRegnrAndTrimSpecs(this._vehicle.specsRaw, trimData)
          : trimData;

        this._vehicleSpecs = data;
        this._vehicle.specs = this._parseSpecs(data);
        this._vehicle.specsRaw = data;
        saveVehicle(this._vehicle);
        this._buildSpecsTable(data);
        this._buildTyreSizeButtons();
        this._syncFiltersFromVehicle();
      }
    } catch (e) {
      console.error('Failed to fetch vehicle specs', e);

      // Keep registration-number data working even if the trim lookup fails.
      if (this._vehicle.specsRaw) {
        const data = this._vehicle.specsRaw;
        this._vehicleSpecs = data;
        this._vehicle.specs = this._parseSpecs(data);
        this._buildSpecsTable(data);
        this._buildTyreSizeButtons();
        this._syncFiltersFromVehicle();
      }
    }
    this._search();
  }

  /**
   * Parse OEM tyre size (e.g. "205/55R16", "225/45ZR17") into { width, profile, diameter }.
   */
  _parseTyreSize(str) {
    const m = (str || '').match(/(\d{3})\/(\d{2,3})\s*[A-Z]*R?\s*(\d{2})/i);
    if (!m) return null;
    return { width: parseInt(m[1], 10), profile: parseInt(m[2], 10), diameter: parseInt(m[3], 10) };
  }

  /**
   * No-op — vehicle tyre filtering is now handled by the tyre size buttons
   * (which set dropdown values on click) and _getVehicleTyreDimensions()
   * (which sends all vehicle sizes as comma-separated API params).
   */
  _syncFiltersFromVehicle() {
    this._vehicleTyre = null;
  }

  _clearTyreSizeSelection(clearDropdowns = false) {
    this._selectedTyreSize = null;
    this._selectedStaggeredPair = null;
    if (this._diameterBar) {
      this._diameterBar.querySelectorAll('.ts__diam-btn').forEach((b) =>
        b.classList.remove('ts__diam-btn--active')
      );
    }
    if (clearDropdowns) {
      if (this._filters.width) this._filters.width.value = '';
      if (this._filters.profile) this._filters.profile.value = '';
      if (this._filters.diameter) this._filters.diameter.value = '';
    }
  }

  // ── Tyre size quick buttons ─────────────────────────────────────────

  _buildTyreSizeButtons() {
    if (!this._diameterBar || !this._vehicleSpecs) {
      return;
    }
    const data = this._vehicleSpecs;
    const chassis = data.DRDChassisReturn;
    if (!chassis) return;

    // Always use staggered-aware builder — it handles both paired
    // (Foran/Bak) and single (Begge) configs correctly, even for
    // vehicles that have a mix of staggered and non-staggered options.
    this._buildStaggeredTyreSizeButtons(data);
  }

  /**
   * Build staggered tyre size buttons — paired "Foran: X | Bak: Y" buttons.
   * Extracts front/rear tyre pairs from each vehicle option (OEM + alternatives).
   */
  _buildStaggeredTyreSizeButtons(data) {
    const chassis = data.DRDChassisReturn;
    if (!chassis) return;

    const pairs = []; // { front, rear, isOem } where front/rear = { width, profile, diameter }
    const singles = []; // non-staggered alternatives
    const pairKeys = new Set();
    const singleKeys = new Set();

    const addConfig = (config, isOem) => {
      const front = this._parseTyreSize(config.TyreSize || config.TyreSizeFront);
      const rear = this._parseTyreSize(config.TyreSizeR || config.TyreSizeRear);
      if (front && rear && (front.width !== rear.width || front.profile !== rear.profile || front.diameter !== rear.diameter)) {
        const fKey = `${front.width}/${front.profile}R${front.diameter}`;
        const rKey = `${rear.width}/${rear.profile}R${rear.diameter}`;
        const pairKey = `${fKey}|${rKey}`;
        if (!pairKeys.has(pairKey)) {
          pairKeys.add(pairKey);
          pairs.push({ front, rear, fKey, rKey, pairKey, isOem });
        }
      } else if (front) {
        const key = `${front.width}/${front.profile}R${front.diameter}`;
        if (!singleKeys.has(key)) {
          singleKeys.add(key);
          singles.push({ key, parsed: front, isOem });
        }
      }
    };

    // OEM chassis
    addConfig(chassis, true);

    // Alternative options
    const modelReturn = data.DRDModelReturn;
    if (modelReturn) {
      if (modelReturn.PrimaryOption) addConfig(modelReturn.PrimaryOption, false);
      if (Array.isArray(modelReturn.Options)) {
        for (const opt of modelReturn.Options) addConfig(opt, false);
      }
    }

    // If the customer selected a wheel diameter before opening the tyre page,
    // only show tyre suggestions with that same diameter. For staggered sets,
    // both front and rear must match the selected diameter.
    const requestedWheelDiam = Number.parseInt(this._pendingWheelDiam || '', 10);
    if (Number.isFinite(requestedWheelDiam)) {
      const matchingPairs = pairs.filter(
        (pair) =>
          pair.front.diameter === requestedWheelDiam &&
          pair.rear.diameter === requestedWheelDiam
      );
      const matchingSingles = singles.filter(
        (single) => single.parsed.diameter === requestedWheelDiam
      );

      // Safety fallback: only replace the list when matching vehicle-approved
      // tyre sizes actually exist. This avoids an empty page if stored data is stale.
      if (matchingPairs.length > 0 || matchingSingles.length > 0) {
        pairs.splice(0, pairs.length, ...matchingPairs);
        singles.splice(0, singles.length, ...matchingSingles);
        this.dataset.wheelDiameterFilter = String(requestedWheelDiam);
      } else {
        delete this.dataset.wheelDiameterFilter;
      }
    } else {
      delete this.dataset.wheelDiameterFilter;
    }

    if (pairs.length === 0 && singles.length === 0) return;

    // Sort pairs by front diameter, then width
    pairs.sort((a, b) => (a.front.diameter - b.front.diameter) || (a.front.width - b.front.width));
    singles.sort((a, b) => (a.parsed.diameter - b.parsed.diameter) || (a.parsed.width - b.parsed.width));

    let html = '';

    // Pair buttons
    for (const p of pairs) {
      const activePair = this._selectedStaggeredPair && this._selectedStaggeredPair._key === p.pairKey;
      const active = activePair ? ' ts__diam-btn--active' : '';
      html += `<button type="button" class="ts__diam-btn ts__diam-btn--pair${active}"
        data-tyre-pair="${p.pairKey}"
        data-front-w="${p.front.width}" data-front-p="${p.front.profile}" data-front-d="${p.front.diameter}"
        data-rear-w="${p.rear.width}" data-rear-p="${p.rear.profile}" data-rear-d="${p.rear.diameter}">
        <span class="ts__diam-top">
          <span class="ts__diam-pair-line"><span class="ts__diam-pair-label">Foran:</span> ${p.fKey}</span>
          <span class="ts__diam-pair-line"><span class="ts__diam-pair-label">Bak:</span> ${p.rKey}</span>
        </span>
      </button>`;
    }

    // Single buttons (non-staggered alternatives)
    for (const s of singles) {
      const active = this._selectedTyreSize === s.key ? ' ts__diam-btn--active' : '';
      html += `<button type="button" class="ts__diam-btn ts__diam-btn--oem${active}" data-tyre-key="${s.key}" data-tyre-width="${s.parsed.width}" data-tyre-profile="${s.parsed.profile}" data-tyre-diam="${s.parsed.diameter}"><span class="ts__diam-top">${s.key}</span></button>`;
    }

    this._diameterBar.innerHTML = html;

    // Auto-select first button if nothing selected yet
    if (!this._selectedTyreSize && !this._selectedStaggeredPair) {
      const savedDiam = this._pendingWheelDiam;
      this._pendingWheelDiam = null;

      let targetBtn = null;
      if (savedDiam) {
        targetBtn = this._diameterBar.querySelector(
          `[data-front-d="${savedDiam}"][data-rear-d="${savedDiam}"], [data-tyre-diam="${savedDiam}"]`
        );
      }

      const allBtns = [
        ...this._diameterBar.querySelectorAll('[data-tyre-pair], [data-tyre-key]')
      ];
      const lastBtn = targetBtn || allBtns[allBtns.length - 1];
      if (lastBtn) lastBtn.click();
    }
  }

  /**
   * Extract all unique tyre size strings from vehicle specs (e.g. "245/45R20").
   * Sent as tyre_sizes param so the API builds proper OR conditions.
   */
  _getVehicleTyreSizeStrings() {
    if (!this._vehicleSpecs) return [];

    const data = this._vehicleSpecs;
    const chassis = data.DRDChassisReturn;
    if (!chassis) return [];

    const sizes = new Set();
    const add = (tyreStr) => {
      const p = this._parseTyreSize(tyreStr);
      if (p) sizes.add(`${p.width}/${p.profile}R${p.diameter}`);
    };

    if (chassis.TyreSize) add(chassis.TyreSize);
    if (chassis.TyreSizeR) add(chassis.TyreSizeR);

    const modelReturn = data.DRDModelReturn;
    if (modelReturn) {
      const addFromOption = (opt) => {
        if (opt.TyreSize) add(opt.TyreSize);
        if (opt.TyreSizeR) add(opt.TyreSizeR);
      };
      if (modelReturn.PrimaryOption) addFromOption(modelReturn.PrimaryOption);
      if (Array.isArray(modelReturn.Options)) {
        for (const opt of modelReturn.Options) addFromOption(opt);
      }
    }

    return [...sizes];
  }

  _parseSpecs(data) {
    const chassis = data.DRDChassisReturn;
    if (chassis) {
      const frontRim = (chassis.RimSize || '').split('x').map((s) => s.trim());
      const rearRim = (chassis.RimSizeR || chassis.RimSize || '').split('x').map((s) => s.trim());
      const hasSplit = (cfg) => {
        if (!cfg) return false;
        const fRim = (cfg.RimSize || '').trim();
        const rRim = (cfg.RimSizeR || '').trim();
        const fTyre = String(cfg.TyreSize || cfg.TyreSizeFront || '').trim();
        const rTyre = String(cfg.TyreSizeR || cfg.TyreSizeRear || '').trim();
        const fEt = String(cfg.RimOffset || '').trim();
        const rEt = String(cfg.OffsetR || '').trim();
        return !!(
          (rRim && rRim !== fRim) ||
          (rTyre && rTyre !== fTyre) ||
          (rEt && rEt !== fEt)
        );
      };

      let fallbackRear = null;
      const modelReturn = data.DRDModelReturn;
      if (modelReturn) {
        const all = [modelReturn.PrimaryOption, ...(modelReturn.Options || [])].filter(Boolean);
        for (const opt of all) {
          if (!hasSplit(opt)) continue;
          const optFront = (opt.RimSize || '').split('x').map((s) => s.trim());
          const optRear = (opt.RimSizeR || '').split('x').map((s) => s.trim());
          fallbackRear = {
            width: parseFloat(optRear[0] || optFront[0] || 0),
            diameter: parseFloat(optRear[1] || optFront[1] || 0),
            tyre: opt.TyreSizeR || opt.TyreSizeRear || '',
          };
          break;
        }
      }

      const chassisHasSplit = hasSplit(chassis);
      const rearWidthFromChassis = parseFloat(rearRim[0] || frontRim[0] || 0);
      const rearDiamFromChassis = parseFloat(rearRim[1] || frontRim[1] || 0);
      const isStaggered = chassisHasSplit || !!fallbackRear;

      return {
        pcd: chassis.PCD || '',
        center_bore: parseFloat(chassis.CentreBore || 0),
        diameter_front: parseFloat(frontRim[1] || 0),
        diameter_rear: chassisHasSplit ? rearDiamFromChassis : (fallbackRear?.diameter || rearDiamFromChassis),
        width_front: parseFloat(frontRim[0] || 0),
        width_rear: chassisHasSplit ? rearWidthFromChassis : (fallbackRear?.width || rearWidthFromChassis),
        is_staggered: isStaggered,
        tyre_front: chassis.TyreSize || '',
        tyre_rear: chassisHasSplit ? (chassis.TyreSizeR || '') : (fallbackRear?.tyre || chassis.TyreSizeR || ''),
      };
    }

    // Fallback
    let front, rear;
    if (Array.isArray(data)) {
      front = data[0] || {};
      rear = data.length > 1 ? data[1] : front;
    } else {
      front = data.wheels?.front || data.front || data;
      rear = data.wheels?.rear || data.rear || front;
    }

    return {
      pcd: front.bolt_pattern || front.pcd || '',
      center_bore: parseFloat(front.center_bore || front.hub_bore || front.bore || 0),
      diameter_front: parseFloat(front.rim_diameter || front.diameter || 0),
      diameter_rear: parseFloat(rear.rim_diameter || rear.diameter || front.rim_diameter || front.diameter || 0),
      width_front: parseFloat(front.rim_width || front.width || 0),
      width_rear: parseFloat(rear.rim_width || rear.width || front.rim_width || front.width || 0),
      is_staggered: false,
      tyre_front: '',
      tyre_rear: '',
    };
  }

  // ── Vehicle header ───────────────────────────────────────────────────

  _showVehicleHeader() {
    if (!this._vehicle) {
      this._showVehicleSelector();
      return;
    }
    const parts = [
      this._vehicle.makeName || this._vehicle.makeSlug,
      this._vehicle.modelName || this._vehicle.modelSlug,
      this._vehicle.year || '',
      this._vehicle.trimName || '',
    ].filter(Boolean);
    this._vehicleLabel.textContent = parts.join(' ').toUpperCase();
    this._headerVehicle.hidden = false;
    this._headerSelector.hidden = true;
    this._updateMobileTireBrandTrigger();
  }

  async _changeVehicle() {
    const v = this._vehicle;
    // Skip transitions when restoring saved vehicle state
    this.dataset.noTransition = '';
    this._showVehicleSelector();
    if (!v?.makeSlug) { delete this.dataset.noTransition; return; }

    if (this._makes.length === 0) await this._loadMakes();
    if (this._hMake) {
      this._hMake.value = v.makeSlug;
      if (!this._hMake.value) this._hMake.selectedIndex = 0;
    }

    if (v.modelSlug) {
      await this._onMakeChange();
      if (this._hModel) this._hModel.value = v.modelSlug;
    }

    if (v.modelSlug && v.year) {
      await this._onModelChange();
      if (this._hYear) this._hYear.value = v.year;
    }

    if (v.modelSlug && v.year && v.trimId) {
      await this._onYearChange();
      if (this._hTrim) this._hTrim.value = v.trimId;
      if (this._hSearch) this._hSearch.disabled = false;
    }

    this._updateMobileSteps();
    // Re-enable transitions after a frame
    requestAnimationFrame(() => { delete this.dataset.noTransition; });
  }

  _showVehicleSelector() {
    this._headerVehicle.hidden = true;
    this._headerSelector.hidden = false;
    if (this._specsPanel) this._specsPanel.hidden = true;
    if (this._specsToggle) {
      this._specsToggle.classList.remove('ts__header-btn--active');
      const si = this._specsToggle.querySelector('.ts__header-btn-top');
      if (si) si.textContent = 'Spesifikasjoner \u25BC';
    }
    if (this._makes.length === 0) this._loadMakes();
  }

  _clearVehicle() {
    clearVehicle();
    sessionStorage.removeItem(this._stateKey);
    this._vehicle = null;
    this._vehicleSpecs = null;
    this._vehicleTyre = null;
    this._clearTyreSizeSelection();
    if (this._diameterBar) this._diameterBar.innerHTML = '';
    if (this._specsPanel) this._specsPanel.hidden = true;
    if (this._specsTable) this._specsTable.querySelector('tbody').innerHTML = '';
    if (this._specsToggle) {
      this._specsToggle.classList.remove('ts__header-btn--active');
      const si = this._specsToggle.querySelector('.ts__header-btn-top');
      if (si) si.textContent = 'Spesifikasjoner \u25BC';
    }
    // Reset all filters
    Object.values(this._filters).forEach((sel) => { if (sel) sel.value = ''; });
    this._resetAccordions();
    Object.values(this._rangeInputs).forEach((input) => { if (input) input.value = ''; });
    this._resetSlider('price');
    // Show inline selector
    this._showVehicleSelector();
    this._resetSelect(this._hModel, 'Modell');
    this._resetSelect(this._hYear, '\u00c5r');
    this._resetSelect(this._hTrim, 'Type');
    if (this._hMake) this._hMake.value = '';
    if (this._hSearch) this._hSearch.disabled = true;
    this._updateMobileSteps();
    // Reset regnr
    if (this._hRegnr) this._hRegnr.value = '';
    if (this._hRegnrSearch) {
      this._hRegnrSearch.disabled = true;
      delete this._hRegnrSearch.dataset.revealed;
    }
    // Update URL
    const urlParams = new URLSearchParams();
    if (this._queryText) urlParams.set('q', this._queryText);
    const qs = urlParams.toString();
    window.history.replaceState({}, '', qs ? `${window.location.pathname}?${qs}` : window.location.pathname);
    this._page = 1;
    this._search();
  }

  // ── Vehicle specs table ─────────────────────────────────────────────

  _buildSpecsTable(data) {
    if (!this._specsTable) return;
    const tbody = this._specsTable.querySelector('tbody');
    if (!tbody) return;

    const chassis = data.DRDChassisReturn;
    if (!chassis) { tbody.innerHTML = ''; return; }

    const pcd = chassis.PCD || '';
    const groups = [];

    // Build spec rows from chassis + model options
    const addRows = (config) => {
      const frontRim = (config.RimSize || '').trim();
      const rearRim = (config.RimSizeR || '').trim();
      if (!frontRim) return;

      const frontTyre = config.TyreSize || config.TyreSizeFront || '';
      const rearTyre = config.TyreSizeR || config.TyreSizeRear || '';
      const fSize = this._formatRimSize(frontRim);
      const rSize = this._formatRimSize(rearRim || frontRim);
      const hasRear = !!(rearRim && rearRim !== frontRim) || !!(rearTyre && rearTyre !== frontTyre);

      if (!hasRear) {
        groups.push({ tyre: frontTyre, rim: fSize, pcd, pos: 'Begge' });
      } else {
        groups.push({ tyre: frontTyre, rim: fSize, pcd, pos: 'Foran' });
        groups.push({ tyre: rearTyre, rim: rSize, pcd, pos: 'Bak' });
      }
    };

    addRows(chassis);
    const modelReturn = data.DRDModelReturn;
    if (modelReturn) {
      if (modelReturn.PrimaryOption) addRows(modelReturn.PrimaryOption);
      if (Array.isArray(modelReturn.Options)) {
        for (const opt of modelReturn.Options) addRows(opt);
      }
    }

    // Deduplicate
    const seen = new Set();
    const html = [];
    for (const r of groups) {
      const key = `${r.tyre}|${r.rim}|${r.pos}`;
      if (seen.has(key)) continue;
      seen.add(key);
      html.push(`<tr>
        <td>${this._esc(r.tyre)}</td>
        <td>${this._esc(r.rim)}</td>
        <td>${this._esc(r.pcd)}</td>
        <td>${this._esc(r.pos)}</td>
      </tr>`);
    }

    tbody.innerHTML = html.join('');
  }

  _formatRimSize(rimStr) {
    const parts = rimStr.split('x').map((s) => s.trim());
    if (parts.length === 2) return `${parts[1]}x${parts[0]}`;
    return rimStr;
  }

  // ── Range sliders ──────────────────────────────────────────────────

  _initSlider(sliderKey, minFilterKey, maxFilterKey) {
    const s = this._sliders[sliderKey];
    if (!s) return;
    const { thumbMin, thumbMax } = s;

    this._updateSliderFill(sliderKey);

    const wireThumb = (e) => {
      const minV = parseFloat(thumbMin.value);
      const maxV = parseFloat(thumbMax.value);
      if (minV > maxV) {
        if (e.target === thumbMin) thumbMin.value = thumbMax.value;
        else thumbMax.value = thumbMin.value;
      }
      this._updateSliderFill(sliderKey);
      const isAtMin = thumbMin.value === thumbMin.min;
      const isAtMax = thumbMax.value === thumbMax.max;
      if (this._rangeInputs[minFilterKey]) this._rangeInputs[minFilterKey].value = isAtMin ? '' : thumbMin.value;
      if (this._rangeInputs[maxFilterKey]) this._rangeInputs[maxFilterKey].value = isAtMax ? '' : thumbMax.value;
      clearTimeout(this._rangeDebounce);
      this._rangeDebounce = setTimeout(() => {
        this._page = 1;
        this._search();
      }, 400);
    };

    thumbMin.addEventListener('input', wireThumb);
    thumbMax.addEventListener('input', wireThumb);
  }

  _updateSliderFill(sliderKey) {
    const s = this._sliders[sliderKey];
    if (!s) return;
    const { thumbMin, thumbMax, fill, valMin, valMax } = s;
    const lo = parseFloat(thumbMin.min);
    const hi = parseFloat(thumbMin.max);
    const range = hi - lo;
    if (!range) return;
    const left = ((thumbMin.value - lo) / range) * 100;
    const right = ((thumbMax.value - lo) / range) * 100;
    fill.style.left = left + '%';
    fill.style.width = (right - left) + '%';
    if (sliderKey === 'price') {
      valMin.textContent = this._formatPrice(thumbMin.value);
      valMax.textContent = this._formatPrice(thumbMax.value);
    } else {
      valMin.textContent = thumbMin.value;
      valMax.textContent = thumbMax.value;
    }
  }

  _formatPrice(val) {
    const n = Math.round(parseFloat(val));
    return n.toLocaleString('nb-NO') + ' kr';
  }

  _resetSlider(sliderKey) {
    const s = this._sliders[sliderKey];
    if (!s) return;
    s.thumbMin.value = s.thumbMin.min;
    s.thumbMax.value = s.thumbMax.max;
    this._updateSliderFill(sliderKey);
  }

  // ── Accordions ──────────────────────────────────────────────────────

  _resetAccordions() {
    Object.entries(this._accordions).forEach(([key, acc]) => {
      if (!acc) return;
      acc.querySelectorAll('input[type="checkbox"]').forEach(cb => { cb.checked = false; });
      const search = acc.querySelector('[data-role="accordion-search"]');
      if (search) search.value = '';
      this._applyCheckboxFilter(key);
      // Collapse all except category
      const body = acc.querySelector('.ts__accordion-body');
      const chevron = acc.querySelector('.ts__accordion-chevron');
      if (key === 'category') {
        if (body) body.classList.remove('is-collapsed');
        if (chevron) chevron.classList.add('is-rotated');
      } else {
        if (body) body.classList.add('is-collapsed');
        if (chevron) chevron.classList.remove('is-rotated');
      }
    });
    // Also collapse slider accordions
    this.querySelectorAll('[data-slider-accordion]').forEach(acc => {
      const body = acc.querySelector('.ts__accordion-body');
      const chevron = acc.querySelector('.ts__accordion-chevron');
      if (body) body.classList.add('is-collapsed');
      if (chevron) chevron.classList.remove('is-rotated');
    });
  }

  _applyCheckboxFilter(key) {
    const acc = this._accordions[key];
    if (!acc) return;
    const list = acc.querySelector('[data-role="checkbox-list"]');
    const searchInput = acc.querySelector('[data-role="accordion-search"]');
    const q = (searchInput?.value || '').trim().toLowerCase();
    if (!list) return;

    list.querySelectorAll('.ts__checkbox-item').forEach(item => {
      if (!q) {
        item.style.display = '';
        return;
      }
      const name = item.querySelector('.ts__checkbox-name');
      const text = (name ? name.textContent : item.textContent).toLowerCase();
      item.style.display = text.includes(q) ? '' : 'none';
    });
  }

  // ── Inline vehicle selector ────────────────────────────────────────

  _initInlineSelector() {
    if (!this._hMake) return;

    this._hMake.addEventListener('change', () => this._onMakeChange());
    this._hModel.addEventListener('change', () => this._onModelChange());
    this._hYear.addEventListener('change', () => this._onYearChange());
    this._hTrim.addEventListener('change', () => {
      if (this._hSearch) this._hSearch.disabled = !this._hTrim.value;
      this._updateMobileSteps();
    });
    if (this._hSearch) {
      this._hSearch.addEventListener('click', () => this._onSelectorSearch());
    }
  }

  _updateMobileSteps() {
    const hasMake = !!this._hMake?.value;
    if (hasMake) this.dataset.makeSelected = '';
    else delete this.dataset.makeSelected;

    const reveal = (el, show) => {
      if (!el) return;
      if (show) el.dataset.revealed = '';
      else delete el.dataset.revealed;
    };
    reveal(this._hModel, !this._hModel?.disabled);
    reveal(this._hYear, !this._hYear?.disabled);
    reveal(this._hTrim, !this._hTrim?.disabled);
    reveal(this._hSearch, !this._hSearch?.disabled);
  }

  async _loadMakes() {
    if (this._makes.length > 0) return;
    try {
      this._makes = (await this._fetchJson('/api/vehicles/makes')) || [];
      this._populateSelect(this._hMake, this._makes, 'Merke', (m) => ({
        value: m.slug,
        label: m.name,
      }));
      this._hMake.disabled = false;
    } catch (e) {
      console.error('Failed to load makes', e);
    }
  }

  async _onMakeChange() {
    const slug = this._hMake.value;
    // Toggle vehicle SØK vs regnr SØK visibility
    if (slug) this.dataset.makeSelected = '';
    else delete this.dataset.makeSelected;
    this._resetSelect(this._hModel, 'Modell');
    this._resetSelect(this._hYear, '\u00c5r');
    this._resetSelect(this._hTrim, 'Type');
    if (this._hSearch) this._hSearch.disabled = true;
    // Clear regnr when make selected
    if (slug && this._hRegnr) {
      this._hRegnr.value = '';
      if (this._hRegnrSearch) {
        this._hRegnrSearch.disabled = true;
        delete this._hRegnrSearch.dataset.revealed;
      }
    }
    this._updateMobileSteps();
    if (!slug) return;
    try {
      this._models = (await this._fetchJson(`/api/vehicles/makes/${slug}`)) || [];
      this._populateSelect(this._hModel, this._models, 'Modell', (m) => {
        return { value: m.slug, label: m.ModelName || m.modelName || m.name || '' };
      });
      this._hModel.disabled = false;
      this._updateMobileSteps();
    } catch (e) {
      console.error('Failed to load models', e);
    }
  }

  async _onModelChange() {
    const makeSlug = this._hMake.value;
    const modelSlug = this._hModel.value;
    this._resetSelect(this._hYear, '\u00c5r');
    this._resetSelect(this._hTrim, 'Type');
    if (this._hSearch) this._hSearch.disabled = true;
    if (!makeSlug || !modelSlug) return;
    try {
      this._years =
        (await this._fetchJson(`/api/vehicles/makes/${makeSlug}/models/${modelSlug}`)) || [];
      this._populateSelect(this._hYear, this._years, '\u00c5r', (y) => ({ value: y, label: y }));
      this._hYear.disabled = false;
      this._updateMobileSteps();
    } catch (e) {
      console.error('Failed to load years', e);
    }
  }

  async _onYearChange() {
    const makeSlug = this._hMake.value;
    const modelSlug = this._hModel.value;
    const year = this._hYear.value;
    this._resetSelect(this._hTrim, 'Type');
    if (this._hSearch) this._hSearch.disabled = true;
    if (!makeSlug || !modelSlug || !year) return;
    try {
      this._trims =
        (await this._fetchJson(`/api/vehicles/makes/${makeSlug}/models/${modelSlug}/years/${year}`)) || [];
      this._populateSelect(this._hTrim, this._trims, 'Type', (t) => {
        const name = t.CarName || t.carName || t.name || '';
        return { value: String(t.CarID || t.carId || t.id || ''), label: name };
      });
      this._hTrim.disabled = false;
      this._updateMobileSteps();
    } catch (e) {
      console.error('Failed to load trims', e);
    }
  }

  _onSelectorSearch() {
    
        sessionStorage.removeItem(this._stateKey);
    const makeSlug = this._hMake.value;
    const modelSlug = this._hModel.value;
    const trimId = this._hTrim.value;
    if (!makeSlug || !modelSlug || !trimId) return;

    const make = this._makes.find((m) => m.slug === makeSlug);
    const model = this._models.find((m) => m.slug === modelSlug);
    const year = this._hYear.value;
    const trim = this._trims.find(
      (t) => String(t.CarID || t.carId || t.id || '') === trimId
    );

    this._vehicle = {
      makeSlug,
      makeName: make?.name || makeSlug,
      modelSlug,
      modelName: (model?.ModelName || model?.modelName || model?.name || modelSlug),
      year,
      trimId,
      trimName: trim?.CarName || trim?.carName || trim?.name || trimId,
    };
    saveVehicle(this._vehicle);

    const urlParams = new URLSearchParams({ make: makeSlug, model: modelSlug, trim: trimId });
    if (this._queryText) urlParams.set('q', this._queryText);
    window.history.replaceState({}, '', `${window.location.pathname}?${urlParams}`);

    this._showVehicleHeader();
    this._page = 1;
    // Reset filters + tyre size buttons
    this._clearTyreSizeSelection();
    if (this._diameterBar) this._diameterBar.innerHTML = '';
    Object.values(this._filters).forEach((sel) => { if (sel) sel.value = ''; });
    this._resetAccordions();
    Object.values(this._rangeInputs).forEach((input) => { if (input) input.value = ''; });
    this._resetSlider('price');
    this._fetchVehicleSpecsThenSearch();
  }

  // ── API search ───────────────────────────────────────────────────────

  async _search() {
    if (this._loading) this._loading.hidden = false;
    if (this._empty) this._empty.hidden = true;
    if (this._grid) this._grid.innerHTML = '';

    const params = new URLSearchParams();
    params.set('limit', String(this._pageSize));
    params.set('page', String(this._page));

    // Only return tyres that are actually available for the selected vehicle
    // and size. This keeps both the result grid and the brand facet/menu aligned.
    params.set('in_stock', 'true');

    if (this._queryText) params.set('q', this._queryText);

    if (this._sort) params.set('sort', this._sort);

    // Tyre size button takes priority — dropdown values may fail on first click
    // before facet options are populated
    if (this._selectedStaggeredPair) {
      const f = this._selectedStaggeredPair.front;
      const r = this._selectedStaggeredPair.rear;
      params.set('tyre_sizes', `${f.width}/${f.profile}R${f.diameter},${r.width}/${r.profile}R${r.diameter}`);
    } else if (this._selectedTyreSize) {
      const parsed = this._parseTyreSize(this._selectedTyreSize);
      if (parsed) {
        params.set('width', String(parsed.width));
        params.set('profile', String(parsed.profile));
        params.set('diameter', String(parsed.diameter));
      }
    } else {
      // Fallback to dropdown filters (or pending from URL params)
      const widthVal = this._filters.width?.value || (this._pendingFilters?.width ?? '');
      if (widthVal) params.set('width', widthVal);
      const profileVal = this._filters.profile?.value || (this._pendingFilters?.profile ?? '');
      if (profileVal) params.set('profile', profileVal);
      const diameterVal = this._filters.diameter?.value || (this._pendingFilters?.diameter ?? '');
      if (diameterVal) params.set('diameter', diameterVal);

      // Vehicle compatibility — send all tyre sizes when no specific filter is active
      if (!widthVal && !profileVal && !diameterVal && this._vehicle && this._vehicleSpecs) {
        const sizes = this._getVehicleTyreSizeStrings();
        if (sizes.length) params.set('tyre_sizes', sizes.join(','));
      }
    }

    // Accordion checkbox filters
    const getChecked = (key) => [...(this._accordions[key]?.querySelectorAll('input:checked') || [])].map(cb => cb.value);
    let categoryVals = getChecked('category');
    if (!this._defaultsApplied && !categoryVals.length && this._defaultCategory) {
      categoryVals = this._defaultCategory.split(',').map(s => s.trim()).filter(Boolean);
    }
    if (categoryVals.length) params.set('category', categoryVals.join(','));
    let brandVals = getChecked('brand');
    if (!this._defaultsApplied && !brandVals.length && this._defaultBrand) {
      brandVals = this._defaultBrand.split(',').map(s => s.trim()).filter(Boolean);
    }
    if (brandVals.length) params.set('brand', brandVals.join(','));
    const tireModelVals = getChecked('tire_model');
    if (tireModelVals.length) params.set('tire_model', tireModelVals.join(','));
    const loadIndexVals = getChecked('load_index');
    if (loadIndexVals.length) params.set('load_index', loadIndexVals.join(','));
    const speedIndexVals = getChecked('speed_index');
    if (speedIndexVals.length) params.set('speed_index', speedIndexVals.join(','));
    const rollingResistanceVals = getChecked('rolling_resistance');
    if (rollingResistanceVals.length) params.set('rolling_resistance', rollingResistanceVals.join(','));
    const wetGripVals = getChecked('wet_grip');
    if (wetGripVals.length) params.set('wet_grip', wetGripVals.join(','));
    const noiseVals = getChecked('noise');
    if (noiseVals.length) params.set('noise', noiseVals.join(','));

    // Range filter inputs (price)
    for (const [key, input] of Object.entries(this._rangeInputs)) {
      const val = input?.value;
      if (val) params.set(key, val);
    }

    // Save state to sessionStorage
    const stateToSave = {
      sort: this._sort,
      selectedTyreSize: this._selectedTyreSize,
      selectedStaggeredPair: this._selectedStaggeredPair,
      width: this._filters.width?.value,
      profile: this._filters.profile?.value,
      diameter: this._filters.diameter?.value,
      category: categoryVals,
      brand: brandVals,
      tire_model: tireModelVals,
      load_index: loadIndexVals,
      speed_index: speedIndexVals,
      rolling_resistance: rollingResistanceVals,
      wet_grip: wetGripVals,
      noise: noiseVals,
      ranges: {}
    };
    for (const [key, input] of Object.entries(this._rangeInputs)) {
      if (input && input.value) stateToSave.ranges[key] = input.value;
    }
    sessionStorage.setItem(this._stateKey, JSON.stringify(stateToSave));

    try {
      const res = await fetch(`${this._api}/api/v2/tires?${params}`);
      if (!res.ok) throw new Error(`API ${res.status}`);
      const json = await res.json();

      if (this._loading) this._loading.hidden = true;

      const products = json.data || [];
      const pagination = json.pagination || {};

      const countText = this._queryText
        ? `${pagination.total || 0} resultater for "${this._queryText}"`
        : `${pagination.total || 0} Resultater`;
      if (this._countEl) this._countEl.textContent = countText;
      if (this._sidebarCount) this._sidebarCount.textContent = `${pagination.total || 0} Resultater`;

      this._updateFacets(json.facets);
      this._renderChips();
      this._defaultsApplied = true;

      if (products.length === 0) {
        if (this._empty) this._empty.hidden = false;
        if (this._pagination) this._pagination.innerHTML = '';
        return;
      }

      this._renderGrid(products);
      this._renderPagination(pagination);
    } catch (e) {
      console.error('Tire search failed', e);
      if (this._loading) this._loading.hidden = true;
      if (this._empty) {
        this._empty.hidden = false;
        const p = this._empty.querySelector('p');
        if (p) p.textContent = 'Noe gikk galt. Pr\u00f8v igjen senere.';
      }
    }
  }

  // ── Render product grid ──────────────────────────────────────────────

  _renderGrid(products) {
    const visibleProducts = products.filter((p) => p.has_stock);

    this._grid.innerHTML = visibleProducts
      .map((p, index) => {
        this._currentTireCardIndex = index;
        return this._cardHtml(p);
      })
      .join('');

    this._hydrateTireCardImages(visibleProducts);
  }

  _productHandle(p) {
    const apiHandle = String(
      p?.group_handle ||
      p?.product_handle ||
      ''
    ).trim();

    if (apiHandle) return apiHandle;

    const brand = String(p?.brand || '').trim();
    const model = String(p?.tire_model || p?.model || '').trim();
    const season = String(p?.season || 'summer').trim();

    // Canonical Shopify product for the ordinary Continental SportContact 7.
    // CSi/FR variants remain separate products.
    const normalizedBrand = brand.toLowerCase().replace(/[^a-z0-9]/g, '');
    const normalizedModel = model.toLowerCase().replace(/[^a-z0-9]/g, '');

    if (
      normalizedBrand === 'continental' &&
      normalizedModel === 'sportcontact7'
    ) {
      return 'continental-sportcontact-7-summer';
    }

    return this._slugify(`${brand}-${model}-${season}`);
  }

  async _hydrateTireCardImages(products) {
    if (!Array.isArray(products) || products.length === 0) return;

    if (!this._tireProductImageCache) {
      this._tireProductImageCache = new Map();
    }

    await Promise.all(
      products.map(async (product, index) => {
        const handle = this._productHandle(product);
        if (!handle) return;

        const image = this._grid.querySelector(
          `[data-tire-card-index="${index}"] [data-tire-card-image]`
        );
        if (!image) return;

        let request = this._tireProductImageCache.get(handle);

        if (!request) {
          request = (async () => {
            try {
              const response = await fetch(
                `/products/${encodeURIComponent(handle)}.js`,
                {
                  credentials: 'same-origin',
                  headers: { Accept: 'application/json' },
                }
              );

              if (!response.ok) return '';

              const shopifyProduct = await response.json();
              return String(
                shopifyProduct?.featured_image ||
                shopifyProduct?.images?.[0] ||
                ''
              ).trim();
            } catch (_) {
              return '';
            }
          })();

          this._tireProductImageCache.set(handle, request);
        }

        const storefrontImage = await request;

        if (
          storefrontImage &&
          image.isConnected &&
          image.src !== new URL(storefrontImage, window.location.href).href
        ) {
          image.onerror = () => {
            image.onerror = null;
            image.src = image.dataset.fallbackSrc;
          };
          image.src = storefrontImage;
        }
      })
    );
  }

  _slugify(text) {
    return (text || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  }

  _cardHtml(p) {
    const handle = this._productHandle(p);
    let href = handle ? `/products/${handle}` : '#';
    if (handle) {
      const qp = new URLSearchParams();
      if (this._selectedStaggeredPair) {
        // Pass staggered dimensions to product page
        const f = this._selectedStaggeredPair.front;
        const r = this._selectedStaggeredPair.rear;
        qp.set('fw', f.width); qp.set('fp', f.profile); qp.set('fd', f.diameter);
        qp.set('rw', r.width); qp.set('rp', r.profile); qp.set('rd', r.diameter);
      } else if (this._selectedTyreSize) {
        const parsed = this._parseTyreSize(this._selectedTyreSize);
        if (parsed) {
          qp.set('width', parsed.width);
          qp.set('profile', parsed.profile);
          qp.set('diameter', parsed.diameter);
        }
      } else {
        const w = this._filters.width?.value || (this._pendingFilters?.width ?? '');
        const pr = this._filters.profile?.value || (this._pendingFilters?.profile ?? '');
        const d = this._filters.diameter?.value || (this._pendingFilters?.diameter ?? '');
        if (w) qp.set('width', w);
        if (pr) qp.set('profile', pr);
        if (d) qp.set('diameter', d);
      }
      const qs = qp.toString();
      if (qs) href += `?${qs}`;
    }
    const fallbackImage = this._tireImageFallback();
    const img = [
      p.main_image,
      p.image,
      p.image_url,
      p.featured_image,
      p.featured_image_url,
    ].find((value) => typeof value === 'string' && value.trim()) || fallbackImage;

    // Available sizes summary: "205-235 / 45-65 R16-19"
    const widths = p.available_widths || [];
    const profiles = p.available_profiles || [];
    const diameters = p.available_diameters || [];

    let sizeStr = '';
    if (widths.length && profiles.length && diameters.length) {
      const w = widths.length === 1 ? `${widths[0]}` : `${widths[0]}-${widths[widths.length - 1]}`;
      const pr = profiles.length === 1 ? `${profiles[0]}` : `${profiles[0]}-${profiles[profiles.length - 1]}`;
      const d = diameters.length === 1 ? `R${diameters[0]}` : `R${diameters[0]}-${diameters[diameters.length - 1]}`;
      sizeStr = `${w}/${pr} ${d}`;
    }

    // Staggered axle annotation
    let axleHtml = '';
    if (this._selectedStaggeredPair) {
      const sp = this._selectedStaggeredPair;
      const matchesFront = widths.includes(sp.front.width) && diameters.includes(sp.front.diameter);
      const matchesRear = widths.includes(sp.rear.width) && diameters.includes(sp.rear.diameter);
      const fLabel = `${sp.front.width}/${sp.front.profile}R${sp.front.diameter}`;
      const rLabel = `${sp.rear.width}/${sp.rear.profile}R${sp.rear.diameter}`;
      if (matchesFront && matchesRear) {
        axleHtml = `<span class="ts__card-axle-rec">Foran: ${this._esc(fLabel)} | Bak: ${this._esc(rLabel)}</span>`;
      } else if (matchesFront) {
        axleHtml = `<span class="ts__card-axle-rec">Foran: ${this._esc(fLabel)}</span><span class="ts__card-axle ts__card-axle--front">Foran</span>`;
      } else if (matchesRear) {
        axleHtml = `<span class="ts__card-axle-rec">Bak: ${this._esc(rLabel)}</span><span class="ts__card-axle ts__card-axle--rear">Bak</span>`;
      }
    }

    // Price
    let priceStr = '';
    if (p.min_price_nok != null) {
      if (p.max_price_nok != null && p.max_price_nok !== p.min_price_nok) {
        priceStr = `fra ${Math.round(p.min_price_nok).toLocaleString('nb-NO')} kr`;
      } else {
        priceStr = `${Math.round(p.min_price_nok).toLocaleString('nb-NO')} kr`;
      }
    }

    // Stock badge
let stockBadge;
if (p.has_stock) {
  stockBadge = '<span class="ts__card-stock ts__card-stock--order">På lager</span>';
} else {
  stockBadge = '<span class="ts__card-stock ts__card-stock--out">Utsolgt</span>';
}

    // Season badge
    let seasonBadge = '';
    const season = (p.season || '').toLowerCase();
    if (season === 'summer') {
      seasonBadge = '<span class="ts__card-season">☀️ Sommer</span>';
    } else if (season === 'studded') {
      seasonBadge = '<span class="ts__card-season">❄️ Pigg</span>';
    } else if (season === 'winter') {
      seasonBadge = '<span class="ts__card-season">❄️ Vinter</span>';
    } else if (season === 'winter-friction') {
      seasonBadge = '<span class="ts__card-season">❄️ Friksjon</span>';
    } else if (season === 'allseason') {
      seasonBadge = '<span class="ts__card-season">🌤️ Helårs</span>';
    }

    // Feature badges (run flat, extra load, snow/ice) — inside image card
    const features = [];
    if (p.has_run_flat) features.push('<span class="ts__feat-badge">Run Flat</span>');
    if (p.has_extra_load) features.push('<span class="ts__feat-badge">Ekstra Last</span>');
    if (p.has_ice_grip) features.push('<span class="ts__feat-badge">Isgrep</span>');
    if (p.has_snow_grip) features.push('<span class="ts__feat-badge">Snøgrep</span>');

    return `
      <a
        class="ts__card"
        href="${this._esc(href)}"
        data-tire-card-index="${this._esc(String(this._currentTireCardIndex ?? 0))}"
        data-product-handle="${this._esc(handle)}"
      >
        <div class="ts__card-img-wrap">
          <img
            class="ts__card-img"
            data-tire-card-image
            src="${this._esc(img)}"
            data-fallback-src="${this._esc(fallbackImage)}"
            alt="${this._esc((p.brand || '') + ' ' + (p.tire_model || ''))}"
            loading="lazy"
            decoding="async"
            onerror="this.onerror=null;this.src=this.dataset.fallbackSrc;"
          >
          <div class="ts__card-badges">
            ${seasonBadge}
            ${features.map(f => f).join('')}
          </div>
        </div>
        <div class="ts__card-body">
          <span class="ts__card-brand">${this._esc(p.brand || '')}</span>
          <span class="ts__card-name">${this._esc(p.tire_model || '')}</span>
          <span class="ts__card-specs">${this._esc(sizeStr)}</span>
          ${axleHtml}
          ${this._cardEuRow(p)}
          <div class="ts__card-footer">
            <span class="ts__card-price">${priceStr}</span>
            ${stockBadge}
          </div>
        </div>
      </a>`;
  }

  _tireImageFallback() {
    const svg = `
      <svg xmlns="http://www.w3.org/2000/svg" width="800" height="800" viewBox="0 0 800 800">
        <rect width="800" height="800" fill="#111111"/>
        <circle cx="400" cy="350" r="190" fill="#242424" stroke="#3a3a3a" stroke-width="18"/>
        <circle cx="400" cy="350" r="95" fill="#111111" stroke="#555555" stroke-width="12"/>
        <path d="M295 205l210 290M245 285l310 130M245 415l310-130M295 495l210-290"
          stroke="#4a4a4a" stroke-width="16" stroke-linecap="round" opacity=".75"/>
        <text x="400" y="650" text-anchor="middle" fill="#c8a051"
          font-family="Arial, sans-serif" font-size="34" font-weight="700">
          BILDE KOMMER
        </text>
      </svg>
    `.trim();

    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
  }

  /** Compact EU label row for product cards */
  _cardEuRow(p) {
    const parts = [];
    if (p.fuel_efficiency) {
      parts.push(
        `<span class="ts__card-eu-item">${this._fuelSvg()}<span class="ts__card-eu-badge ts__card-eu-badge--fuel-${this._esc(p.fuel_efficiency)}">${this._esc(p.fuel_efficiency)}</span></span>`
      );
    }
    if (p.wet_grip) {
      parts.push(
        `<span class="ts__card-eu-item">${this._rainSvg()}<span class="ts__card-eu-badge ts__card-eu-badge--wet-${this._esc(p.wet_grip)}">${this._esc(p.wet_grip)}</span></span>`
      );
    }
    if (p.noise_class) {
      const db = p.noise_value ? `${p.noise_value}dB` : '';
      parts.push(
        `<span class="ts__card-eu-item">${this._speakerSvg()}<span class="ts__card-eu-badge ts__card-eu-badge--noise">${this._esc(p.noise_class)}${db ? '|' + db : ''}</span></span>`
      );
    }
    if (parts.length === 0) return '';
    return `<div class="ts__card-eu">${parts.join('')}</div>`;
  }

  // ── Pagination ───────────────────────────────────────────────────────

  _renderPagination(pagination) {
    const { page, total_pages } = pagination;
    if (total_pages <= 1) {
      this._pagination.innerHTML = '';
      return;
    }

    const buttons = [];
    buttons.push(
      `<button class="ts__page-btn" data-page="${page - 1}" ${page <= 1 ? 'disabled' : ''}>&larr;</button>`
    );

    const start = Math.max(1, page - 3);
    const end = Math.min(total_pages, page + 3);
    if (start > 1) {
      buttons.push(`<button class="ts__page-btn" data-page="1">1</button>`);
      if (start > 2) buttons.push(`<span style="padding:0.35rem">\u2026</span>`);
    }
    for (let i = start; i <= end; i++) {
      const active = i === page ? ' ts__page-btn--active' : '';
      buttons.push(`<button class="ts__page-btn${active}" data-page="${i}">${i}</button>`);
    }
    if (end < total_pages) {
      if (end < total_pages - 1) buttons.push(`<span style="padding:0.35rem">\u2026</span>`);
      buttons.push(`<button class="ts__page-btn" data-page="${total_pages}">${total_pages}</button>`);
    }

    buttons.push(
      `<button class="ts__page-btn" data-page="${page + 1}" ${page >= total_pages ? 'disabled' : ''}>&rarr;</button>`
    );

    this._pagination.innerHTML = buttons.join('');
    this._pagination.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-page]');
      if (!btn || btn.disabled) return;
      this._page = parseInt(btn.dataset.page, 10);
      this._search();
      this.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  // ── Update sidebar filters from API facets ──────────────────────────

  _updateFacets(facets) {
    if (!facets) return;
    this._updateFacetSelect(this._filters.width, facets.width);
    this._updateFacetSelect(this._filters.profile, facets.profile);
    this._updateFacetSelect(this._filters.diameter, facets.diameter || facets.size, (v) => `${v}"`);

    // Apply pending URL filters now that dropdown options exist
    if (this._pendingFilters) {
      if (this._pendingFilters.width && this._filters.width) this._filters.width.value = this._pendingFilters.width;
      if (this._pendingFilters.profile && this._filters.profile) this._filters.profile.value = this._pendingFilters.profile;
      if (this._pendingFilters.diameter && this._filters.diameter) this._filters.diameter.value = this._pendingFilters.diameter;
      this._pendingFilters = null;
    }

    // Sync dropdowns from tyre size button selection (dropdown set may have
    // failed on first click before facet options were populated)
    if (this._selectedTyreSize) {
      const parsed = this._parseTyreSize(this._selectedTyreSize);
      if (parsed) {
        if (this._filters.width) this._filters.width.value = String(parsed.width);
        if (this._filters.profile) this._filters.profile.value = String(parsed.profile);
        if (this._filters.diameter) this._filters.diameter.value = String(parsed.diameter);
      }
    }

    this._updateFacetCheckboxes('category', facets.category);

    if (Array.isArray(facets.brand)) {
      this._tireBrandFacetItems = facets.brand;
    }
    this._updateFacetCheckboxes('brand', facets.brand);
    this._renderMobileTireBrandSheet();
    this._updateMobileTireBrandTrigger();

    this._updateFacetCheckboxes('tire_model', facets.tire_model || facets.model);
    this._updateFacetCheckboxes('load_index', facets.load_index);
    this._updateFacetCheckboxes('speed_index', facets.speed_index);
    this._updateFacetCheckboxes('rolling_resistance', facets.rolling_resistance || facets.fuel_efficiency);
    this._updateFacetCheckboxes('wet_grip', facets.wet_grip);
    this._updateFacetCheckboxes('noise', facets.noise || facets.noise_class);
  }

  _updateFacetSelect(select, facetItems, labelFn) {
    if (!select || !facetItems) return;
    const current = select.value;
    const placeholder = select.options[0]?.textContent || 'Alle';

    const options = [`<option value="">${this._esc(placeholder)}</option>`];
    for (const item of facetItems) {
      const label = labelFn ? labelFn(item.value, item.count) : item.value;
      options.push(`<option value="${item.value}">${this._esc(String(label))} (${item.count})</option>`);
    }
    select.innerHTML = options.join('');

    if (current) {
      const exists = facetItems.some((item) => String(item.value) === String(current));
      if (exists) select.value = current;
    }
  }

  _updateFacetCheckboxes(key, facetItems) {
    const acc = this._accordions[key];
    if (!acc || !facetItems) return;
    const list = acc.querySelector('[data-role="checkbox-list"]');
    if (!list) return;

    const checked = new Set([...(list.querySelectorAll('input:checked') || [])].map(cb => cb.value));

    // Apply restored session state
    if (this._restoredAccordions && this._restoredAccordions[key]) {
      this._restoredAccordions[key].forEach(val => checked.add(val));
      delete this._restoredAccordions[key];
    }

    // On first render, pre-check defaults
    let defaultSet = null;
    if (!this._defaultsApplied) {
      if (key === 'category' && this._defaultCategory) {
        this._defaultCategory.split(',').map(s => s.trim()).filter(Boolean).forEach(v => checked.add(v));
      }
      if (key === 'brand' && this._defaultBrand) {
        defaultSet = new Set(this._defaultBrand.split(',').map(s => s.trim().toUpperCase()).filter(Boolean));
      }
    }

    const items = [];
    for (const item of facetItems) {
      const val = String(item.value);
      const matchesDefault = defaultSet && defaultSet.has(val.toUpperCase());
      if (matchesDefault) checked.add(val);
      const isChecked = checked.has(val) ? ' checked' : '';
      const label = key === 'category' ? (SEASON_LABELS[val] || val) : val;

      let nameHtml;
      if (key === 'rolling_resistance') {
        nameHtml = `<span class="ts__eu-arrow ts__eu-arrow--fuel-${this._esc(val)}">${this._esc(val)}</span>`;
      } else if (key === 'wet_grip') {
        nameHtml = `<span class="ts__eu-arrow ts__eu-arrow--wet-${this._esc(val)}">${this._esc(val)}</span>`;
      } else if (key === 'noise') {
        nameHtml = `<span class="ts__eu-noise">${this._noiseSvg(val)} ${this._esc(val)}</span>`;
      } else {
        nameHtml = `<span class="ts__checkbox-name">${this._esc(label)}</span>`;
      }

      items.push(
        `<label class="ts__checkbox-item" data-index="${items.length}">` +
        `<input type="checkbox" value="${this._esc(val)}"${isChecked}>` +
        nameHtml +
        `<span class="ts__checkbox-count">${item.count}</span>` +
        `</label>`
      );
    }
    list.innerHTML = items.join('');

    this._applyCheckboxFilter(key);
  }

  // ── Active filter chips ──────────────────────────────────────────────

  _renderChips() {
    if (!this._activeFiltersEl) return;

    const labels = {
      category: 'Kategori',
      brand: 'Merke',
      tire_model: 'Modell',
      load_index: 'Belastning',
      speed_index: 'Hastighet',
      rolling_resistance: 'Rullemotstand',
      wet_grip: 'V\u00e5tgrep',
      noise: 'St\u00f8y',
    };

    const dropdownLabels = {
      width: 'Bredde',
      profile: 'Profil',
      diameter: 'Diameter',
    };

    const chips = [];

    // Accordion checkbox filters
    for (const [key, label] of Object.entries(labels)) {
      const acc = this._accordions[key];
      if (!acc) continue;
      const checked = [...acc.querySelectorAll('input[type="checkbox"]:checked')];
      for (const cb of checked) {
        const val = cb.value;
        const displayVal = key === 'category' ? (SEASON_LABELS[val] || val) : val;
        chips.push({ type: 'checkbox', key, value: val, label: `${label}: ${displayVal}` });
      }
    }

    // Dropdown filters
    for (const [key, label] of Object.entries(dropdownLabels)) {
      const sel = this._filters[key];
      if (sel && sel.value) {
        const display = key === 'diameter' ? `${sel.value}"` : sel.value;
        chips.push({ type: 'dropdown', key, value: sel.value, label: `${label}: ${display}` });
      }
    }

    // Tyre size button
    if (this._selectedTyreSize) {
      chips.push({ type: 'tyresize', key: 'tyre_size', value: this._selectedTyreSize, label: `Dekkstr: ${this._selectedTyreSize}` });
    }

    // Price range
    const minP = this._rangeInputs.min_price?.value;
    const maxP = this._rangeInputs.max_price?.value;
    if (minP) chips.push({ type: 'range', key: 'min_price', value: null, label: `Pris fra ${parseInt(minP).toLocaleString('nb-NO')} kr` });
    if (maxP) chips.push({ type: 'range', key: 'max_price', value: null, label: `Pris til ${parseInt(maxP).toLocaleString('nb-NO')} kr` });

    if (chips.length === 0) {
      this._activeFiltersEl.innerHTML = '';
      return;
    }

    this._activeFiltersEl.innerHTML = chips.map((c, i) =>
      `<span class="ts__chip">
        <span class="ts__chip-label">${this._esc(c.label)}</span>
        <button type="button" class="ts__chip-x" data-chip-idx="${i}" aria-label="Fjern">&times;</button>
      </span>`
    ).join('');

    for (const btn of this._activeFiltersEl.querySelectorAll('.ts__chip-x')) {
      btn.addEventListener('click', () => {
        const idx = parseInt(btn.dataset.chipIdx, 10);
        const chip = chips[idx];
        if (chip) this._removeChipFilter(chip);
      });
    }
  }

  _removeChipFilter(chip) {
    if (chip.type === 'checkbox') {
      const acc = this._accordions[chip.key];
      if (acc) {
        const cb = acc.querySelector(`input[type="checkbox"][value="${CSS.escape(chip.value)}"]`);
        if (cb) cb.checked = false;
      }
    } else if (chip.type === 'dropdown') {
      const sel = this._filters[chip.key];
      if (sel) sel.value = '';
    } else if (chip.type === 'tyresize') {
      this._clearTyreSizeSelection(true);
    } else if (chip.key === 'min_price') {
      if (this._rangeInputs.min_price) this._rangeInputs.min_price.value = '';
      this._resetSlider('price');
    } else if (chip.key === 'max_price') {
      if (this._rangeInputs.max_price) this._rangeInputs.max_price.value = '';
      this._resetSlider('price');
    }
    this._page = 1;
    this._search();
  }

  // ── Shared helpers ───────────────────────────────────────────────────

  _esc(str) {
    const d = document.createElement('div');
    d.textContent = String(str);
    return d.innerHTML;
  }

  /** SVG speaker icon with 1/2/3 sound waves based on noise grade (A/B/C or 1/2/3) */
  _noiseSvg(grade) {
    const g = (grade || '').toUpperCase();
    const wave1 = '<path d="M15 8.5a3 3 0 0 1 0 5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" fill="none"/>';
    const wave2 = '<path d="M17.5 6a6.5 6.5 0 0 1 0 10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" fill="none"/>';
    const wave3 = '<path d="M20 3.5a10 10 0 0 1 0 15" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" fill="none"/>';
    let waves = wave1;
    if (g === 'B' || g === '2') waves = wave1 + wave2;
    if (g === 'C' || g === '3') waves = wave1 + wave2 + wave3;
    return `<svg viewBox="0 0 24 22" fill="none"><path d="M3 8.5h2.5L11 4v14l-5.5-4.5H3a1 1 0 0 1-1-1v-3a1 1 0 0 1 1-1z" fill="currentColor"/>${waves}</svg>`;
  }

  /** Fuel pump SVG icon (14px) */
  _fuelSvg() {
    return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="12" height="18" rx="1"/><path d="M15 12h2a2 2 0 0 1 2 2v3a1 1 0 0 0 2 0V8l-3-3"/><path d="M7 3v5"/></svg>';
  }

  /** Rain/wet grip SVG icon (14px) */
  _rainSvg() {
    return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2a5 5 0 0 0-5 5 5 5 0 0 0-.5 0A4.5 4.5 0 1 0 6.5 16h11a4 4 0 0 0 .5-7.97A5 5 0 0 0 12 2z"/><path d="M8 19v2M12 19v2M16 19v2"/></svg>';
  }

  /** Speaker SVG icon (14px) for card */
  _speakerSvg() {
    return '<svg viewBox="0 0 24 22" fill="none"><path d="M3 8.5h2.5L11 4v14l-5.5-4.5H3a1 1 0 0 1-1-1v-3a1 1 0 0 1 1-1z" fill="currentColor" stroke="currentColor" stroke-width="0.5"/><path d="M15 8.5a3 3 0 0 1 0 5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" fill="none"/></svg>';
  }

  async _fetchJson(path) {
    const res = await fetch(`${this._api}${path}`);
    if (!res.ok) throw new Error(`API ${res.status}`);
    const json = await res.json();
    return json.data ?? json;
  }

  _populateSelect(select, items, placeholder, mapFn) {
    const options = [`<option value="">${placeholder}</option>`];
    for (const item of items) {
      const { value, label } = mapFn(item);
      options.push(`<option value="${this._esc(String(value))}">${this._esc(label)}</option>`);
    }
    select.innerHTML = options.join('');
  }

  _resetSelect(select, placeholder) {
    select.innerHTML = `<option value="">${placeholder}</option>`;
    select.disabled = true;
  }
}

if (!customElements.get('tire-search')) {
  customElements.define('tire-search', TireSearchElement);
}

